import React, { Component, ReactNode } from 'react';
import { CzMaxPrice } from '~/Api/User/ProductGroup/CzMaxPrice';
import { ProductPrice } from '~/Product';
import { formatCountriesError } from '~/UI';
import { Link } from '~/UI/Button';
import CountryIcon from '~/UI/CountryIcon';
import Heading from '~/UI/Heading';
import Tooltip from '~/UI/Tooltip';

import styles from './styles.less';

interface Props {
	czMaxPrice: CzMaxPrice;
	czechExFactoryPrice: ProductPrice | null;
	formatPercentage(percentage: number): string;
}

export default class Calculation extends Component<Props> {
	render() {
		const {
			czMaxPrice: { finalCalculation },
			czechExFactoryPrice,
			formatPercentage,
		} = this.props;

		return (
			<div className={styles.container}>
				<div className={styles.columns}>
					<div className={styles.leftColumn}>
						<Heading level={3}>Lowest ex-factory prices:</Heading>
						{finalCalculation.fromProducts.map((product, i) => (
							<div
								className={styles.row}
								data-product-id={product.id}
								data-testid={`lowest-exfactory-price-${i}`}
								key={product.id}
							>
								<span className={styles.number}>{i + 1}.</span>

								<span className={styles.countryIcon}>
									<CountryIcon code={product.dataSource.country.alpha2} name={product.dataSource.country.name} />
								</span>

								<span className={styles.countryDescription}>
									{product.dataSource.country.alpha2}
									<br />
									{product.dataSource.shortName}
								</span>
							</div>
						))}
					</div>
					<div className={styles.centerColumn}>
						<Heading level={3}>Calculated max price:</Heading>
						<div className={styles.row}>
							<p className={styles.rowName}>Average of 3 lowest ex-factory prices:</p>
							<p className={styles.rowValue}>
								{finalCalculation.threeLowestPricesAvg ? (
									<strong data-testid='average-of-3-lowest-ex-factory-prices'>
										{finalCalculation.threeLowestPricesAvg.roundedHumanFormat()}
									</strong>
								) : null}
							</p>
						</div>
						<div className={styles.row}>
							<p className={styles.rowName}>2. + 3. lowest price:</p>
							<p className={styles.rowValue}>
								{finalCalculation.secondAndThirdLowestPriceAvg ? (
									<strong data-testid='2-3-lowest-price'>
										{finalCalculation.secondAndThirdLowestPriceAvg.roundedHumanFormat()}
									</strong>
								) : null}
							</p>
						</div>
						<div className={styles.row}>
							<p className={styles.rowName}>Deviation:</p>
							<p className={styles.rowValue}>
								{finalCalculation.deviation ? (
									<strong data-testid='deviation'>{formatPercentage(finalCalculation.deviation)}</strong>
								) : null}
							</p>
						</div>
						{finalCalculation.emptyResultReason.length > 0 ? (
							<div className={styles.row}>
								<p>{this.renderEmptyResultReason()}</p>
							</div>
						) : null}
					</div>
					<div className={styles.rightColumn}>
						<Heading level={3}>&nbsp;</Heading>
						<div className={styles.row}>
							<p className={styles.smallRowName}>Current price (SCAU):</p>
							<p className={styles.smallRowValue}>
								{czechExFactoryPrice !== null ? (
									<strong data-testid='current-price-scau'>{czechExFactoryPrice.price?.roundedHumanFormat()}</strong>
								) : (
									this.renderNoCzechProductTooltip()
								)}
							</p>
						</div>
					</div>
				</div>
			</div>
		);
	}

	renderNoCzechProductTooltip(): ReactNode {
		return (
			<Tooltip reference={<strong className={styles.tip}>No info on CZ product</strong>}>
				Use <Link to='/product-groups/'>search</Link> to add czech product to this group.
			</Tooltip>
		);
	}

	renderEmptyResultReason(): ReactNode {
		const {
			czMaxPrice: { finalCalculation },
		} = this.props;
		const countryError = finalCalculation.findOnlyOneProductPerCountryErrorCode();

		if (countryError !== null) {
			return (
				<strong className={styles.rowError}>
					Calculation requires at most one product for each country
					{formatCountriesError(countryError.duplicateCountries)}
				</strong>
			);
		}
		if (finalCalculation.hasMissingProductSettings()) {
			return <strong className={styles.rowError}>Some products settings are missing</strong>;
		}
		if (finalCalculation.isReferenceCountryProductMissing()) {
			return <strong className={styles.rowError}>Calculation requires at least one reference country product</strong>;
		}
		if (finalCalculation.isMissingReferenceSettings()) {
			return <strong className={styles.rowError}>Calculation requires reference settings</strong>;
		}
		if (finalCalculation.isMissingThreeReferenceCountryProducts()) {
			return <strong className={styles.rowError}>Calculation requires three reference country products</strong>;
		}
		if (finalCalculation.isIllegalProductPriceTypeSelected()) {
			return <strong className={styles.rowError}>Some products have illegal price selected</strong>;
		}
		return null;
	}
}
